import {
  Component,
  ElementRef,
  HostListener,
  OnDestroy,
  OnInit,
  Renderer2,
  ViewChild,
  inject,
  ChangeDetectionStrategy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Subscription, from, of } from 'rxjs';
import { catchError, concatMap, map, toArray } from 'rxjs/operators';
import { NotificationService } from '../../../../services/notification.service';
import { CompanyContextService } from '../../../services/company-context.service';
import {
  FeedbackConfigurationRow,
  FeedbackConfigurationService,
} from '../../../services/feedback-configuration.service';
import {
  FEEDBACK_PUBLIC_ORIGIN_UNCONFIGURED,
  buildFeedbackUrlPreview,
  resolveFeedbackSlugPreview,
} from '../../../../feedback/feedback-branding.util';
import { sanitizeGoogleReviewUrl } from '../../../../feedback/feedback-form-page.component';

const DEFAULT_DELAY_MINUTES = 180;
const MAX_SMS_MESSAGE_LENGTH = 320;

type ModalMode = 'add' | 'edit';

function buildDefaultFeedbackSmsMessage(shopName: string): string {
  const name = shopName.trim() || 'your car wash';
  return `Thanks for visiting ${name}. We'd love your feedback. Click to let us know how we did!`;
}

function parseDelayMinutes(value: unknown): number {
  const parsed = Number.parseInt(String(value ?? ''), 10);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
}

function deriveFeedbackSlugFromName(value: string): string {
  const base = String(value || '')
    .trim()
    .replace(/\s+/g, '');
  return base || 'Location';
}

@Component({
  selector: 'app-company-feedback-configuration',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './company-feedback-configuration.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./company-feedback-configuration.component.scss'],
})
export class CompanyFeedbackConfigurationComponent implements OnInit, OnDestroy {
  private readonly context = inject(CompanyContextService);
  private readonly feedbackService = inject(FeedbackConfigurationService);
  private readonly notifications = inject(NotificationService);
  private readonly renderer = inject(Renderer2);

  locations: FeedbackConfigurationRow[] = [];
  loading = true;
  errorMessage = '';
  openMenuLocationId: string | null = null;

  modalOpen = false;
  modalMode: ModalMode = 'add';
  editingLocationId = '';
  pickerOpen = false;
  selectedLocationIds: string[] = [];
  draftUrl = '';
  draftMinutes: string | number = DEFAULT_DELAY_MINUTES;
  draftMessage = '';
  urlTouched = false;
  messageTouched = false;
  minutesTouched = false;
  saving = false;

  private contextSub?: Subscription;
  private listSub?: Subscription;
  private saveSub?: Subscription;
  private companyId = '';
  private modalElement: HTMLElement | null = null;
  private bodyScrollBackup = '';

  @ViewChild('modalOverlay')
  set modalOverlay(element: ElementRef<HTMLElement> | undefined) {
    if (!element?.nativeElement) {
      return;
    }

    this.modalElement = element.nativeElement;
    this.attachModalToBody(this.modalElement);
  }

  ngOnInit(): void {
    this.contextSub = this.context.current$.subscribe(company => {
      const nextCompanyId = company?.id || '';
      if (nextCompanyId !== this.companyId) {
        this.companyId = nextCompanyId;
        this.closeModal();
        this.loadLocations();
      }
    });
  }

  ngOnDestroy(): void {
    this.contextSub?.unsubscribe();
    this.listSub?.unsubscribe();
    this.saveSub?.unsubscribe();
    if (this.modalElement?.parentElement === document.body) {
      this.renderer.removeChild(document.body, this.modalElement);
    }
    this.restoreBodyScroll();
  }

  get configuredRows(): FeedbackConfigurationRow[] {
    return this.locations.filter(row => row.is_configured);
  }

  get companyName(): string {
    return this.context.name || 'this car wash';
  }

  get modalTitle(): string {
    return this.modalMode === 'edit' ? 'Edit feedback configuration' : 'Add feedback configuration';
  }

  get modalSubtitle(): string {
    return this.modalMode === 'edit'
      ? 'Update the SMS message, Google review URL, or send delay for this location.'
      : 'Choose one or more locations and customize the feedback SMS message and timing.';
  }

  get showLocationPicker(): boolean {
    return this.modalMode === 'add';
  }

  get pickerLabel(): string {
    if (!this.selectedLocationIds.length) {
      return 'Select locations';
    }
    if (this.selectedLocationIds.length === 1) {
      return this.locationLabel(this.selectedLocationIds[0]);
    }
    return `${this.selectedLocationIds.length} locations selected`;
  }

  get toggleAllLabel(): string {
    const selectable = this.selectableLocationIds;
    const allSelected =
      selectable.length > 0 && selectable.every(id => this.selectedLocationIds.includes(id));
    return allSelected ? 'Clear all' : 'Select all';
  }

  get urlError(): boolean {
    const url = this.draftUrl.trim();
    return this.urlTouched && url.length > 0 && !sanitizeGoogleReviewUrl(url);
  }

  get messageError(): boolean {
    const message = this.draftMessage.trim();
    return this.messageTouched && (message.length === 0 || message.length > MAX_SMS_MESSAGE_LENGTH);
  }

  get minutesError(): boolean {
    return this.minutesTouched && parseDelayMinutes(this.draftMinutes) <= 0;
  }

  get canSave(): boolean {
    const url = sanitizeGoogleReviewUrl(this.draftUrl);
    const message = this.draftMessage.trim();
    const minutes = parseDelayMinutes(this.draftMinutes);
    const hasLocations = this.modalMode === 'edit' || this.selectedLocationIds.length > 0;
    return (
      hasLocations &&
      Boolean(url) &&
      message.length > 0 &&
      message.length <= MAX_SMS_MESSAGE_LENGTH &&
      minutes > 0 &&
      !this.saving
    );
  }

  get previewFeedbackSlug(): string {
    const locationId =
      this.modalMode === 'edit' ? this.editingLocationId : this.selectedLocationIds[0];
    if (locationId) {
      const row = this.locations.find(location => location.id === locationId);
      if (row) {
        return resolveFeedbackSlugPreview(row, this.locations);
      }
    }

    return deriveFeedbackSlugFromName(this.companyName);
  }

  get previewFeedbackUrl(): string {
    return buildFeedbackUrlPreview(this.previewFeedbackSlug);
  }

  get isFeedbackPublicOriginConfigured(): boolean {
    return this.previewFeedbackUrl !== FEEDBACK_PUBLIC_ORIGIN_UNCONFIGURED;
  }

  @HostListener('document:click')
  onDocumentClick(): void {
    this.openMenuLocationId = null;
  }

  locationLabel(locationId: string): string {
    return this.locations.find(row => row.id === locationId)?.branch?.trim() || locationId;
  }

  timingLabel(minutes: number): string {
    const value = parseDelayMinutes(minutes);
    return `${value} ${value === 1 ? 'min' : 'mins'}`;
  }

  messagePreview(message: string | null | undefined): string {
    const trimmed = String(message || '').trim();
    return trimmed || '—';
  }

  isLocationConfigured(locationId: string): boolean {
    return this.locations.some(row => row.id === locationId && row.is_configured);
  }

  isLocationSelected(locationId: string): boolean {
    return this.selectedLocationIds.includes(locationId);
  }

  toggleMenu(locationId: string, event: Event): void {
    event.stopPropagation();
    this.openMenuLocationId = this.openMenuLocationId === locationId ? null : locationId;
  }

  openAddModal(): void {
    this.modalMode = 'add';
    this.editingLocationId = '';
    this.selectedLocationIds = [];
    this.resetAddDraftDefaults();
    this.pickerOpen = false;
    this.modalOpen = true;
  }

  openEditModal(row: FeedbackConfigurationRow, event?: Event): void {
    event?.stopPropagation();
    this.modalMode = 'edit';
    this.editingLocationId = row.id;
    this.selectedLocationIds = [row.id];
    this.applyLocationDraft(row);
    this.pickerOpen = false;
    this.openMenuLocationId = null;
    this.modalOpen = true;
  }

  closeModal(): void {
    this.modalOpen = false;
    this.pickerOpen = false;
    this.saving = false;
    this.modalElement = null;
    this.restoreBodyScroll();
  }

  togglePicker(event: Event): void {
    event.stopPropagation();
    this.pickerOpen = !this.pickerOpen;
  }

  toggleLocationSelection(locationId: string, event: Event): void {
    event.stopPropagation();

    if (this.isLocationSelected(locationId)) {
      this.selectedLocationIds = this.selectedLocationIds.filter(id => id !== locationId);
      this.syncDraftForSelectedLocations();
      return;
    }

    this.selectedLocationIds = [...this.selectedLocationIds, locationId];
    this.syncDraftForSelectedLocations();
  }

  toggleSelectAll(event: Event): void {
    event.stopPropagation();
    const selectable = this.selectableLocationIds;
    const allSelected = selectable.every(id => this.selectedLocationIds.includes(id));
    this.selectedLocationIds = allSelected ? [] : [...selectable];
    this.syncDraftForSelectedLocations();
  }

  onDraftUrlChange(value: string): void {
    this.draftUrl = value;
    this.urlTouched = true;
  }

  onDraftMessageChange(value: string): void {
    this.draftMessage = value;
    this.messageTouched = true;
  }

  onDraftMinutesChange(value: string | number): void {
    this.draftMinutes = value;
    this.minutesTouched = true;
  }

  saveModal(): void {
    if (!this.canSave || !this.companyId) {
      return;
    }

    const googleReviewUrl = sanitizeGoogleReviewUrl(this.draftUrl);
    const feedbackSmsMessage = this.draftMessage.trim();
    if (!googleReviewUrl) {
      this.urlTouched = true;
      return;
    }
    if (!feedbackSmsMessage || feedbackSmsMessage.length > MAX_SMS_MESSAGE_LENGTH) {
      this.messageTouched = true;
      return;
    }

    const locationIds =
      this.modalMode === 'edit' ? [this.editingLocationId] : [...this.selectedLocationIds];
    const feedbackSmsDelayMinutes = parseDelayMinutes(this.draftMinutes);
    if (!feedbackSmsDelayMinutes) {
      this.minutesTouched = true;
      return;
    }

    const payload = {
      google_review_url: googleReviewUrl,
      feedback_sms_delay_minutes: feedbackSmsDelayMinutes,
      feedback_sms_message: feedbackSmsMessage,
    };

    this.saving = true;
    this.saveSub?.unsubscribe();
    this.saveSub = from(locationIds)
      .pipe(
        concatMap(locationId =>
          this.feedbackService.saveConfiguration(this.companyId, locationId, payload).pipe(
            map(response => ({
              locationId,
              success: Boolean(response.status),
              message: response.message,
              data: response.data ?? null,
            })),
            catchError(err =>
              of({
                locationId,
                success: false,
                message:
                  err?.error?.message || err?.message || 'Failed to save feedback configuration.',
                data: null,
              })
            )
          )
        ),
        toArray()
      )
      .subscribe({
        next: results => {
          this.saving = false;
          const succeeded = results.filter(result => result.success);
          const failed = results.filter(result => !result.success);

          if (succeeded.length) {
            this.locations = this.locations.map(location => {
              const saved = succeeded.find(result => result.locationId === location.id);
              return saved?.data ? { ...location, ...saved.data } : location;
            });
          }

          if (!failed.length) {
            this.notifications.showSuccess(
              locationIds.length === 1
                ? 'Feedback configuration saved.'
                : `Feedback configuration saved for ${locationIds.length} locations.`
            );
            this.closeModal();
            this.loadLocations();
            return;
          }

          if (succeeded.length) {
            const failedNames = failed
              .map(result => this.locationLabel(result.locationId))
              .join(', ');
            this.notifications.showError(
              `Saved ${succeeded.length} location(s), but failed for: ${failedNames}. List refreshed.`
            );
            this.loadLocations();
            return;
          }

          this.notifications.showError(
            failed[0]?.message || 'Unable to save feedback configuration.'
          );
        },
      });
  }

  disableConfiguration(row: FeedbackConfigurationRow, event?: Event): void {
    event?.stopPropagation();
    if (!this.companyId) {
      return;
    }

    this.openMenuLocationId = null;
    this.saveSub?.unsubscribe();
    this.saveSub = this.feedbackService.disableConfiguration(this.companyId, row.id).subscribe({
      next: response => {
        if (!response.status) {
          this.notifications.showError(
            response.message || 'Unable to disable feedback configuration.'
          );
          return;
        }

        this.notifications.showSuccess('Feedback configuration disabled.');
        this.loadLocations();
      },
      error: err => {
        this.notifications.showError(
          err?.error?.message || err?.message || 'Failed to disable feedback configuration.'
        );
      },
    });
  }

  private get selectableLocationIds(): string[] {
    return this.locations.map(row => row.id);
  }

  private syncDraftForSelectedLocations(): void {
    if (this.modalMode !== 'add') {
      return;
    }

    if (!this.selectedLocationIds.length || this.selectedLocationIds.length !== 1) {
      this.resetAddDraftDefaults();
      return;
    }

    const row = this.locations.find(location => location.id === this.selectedLocationIds[0]);
    if (!row) {
      return;
    }

    if (row.is_configured) {
      this.applyLocationDraft(row);
      return;
    }

    this.resetAddDraftDefaults(row.branch || this.companyName);
  }

  private resetAddDraftDefaults(messageName?: string): void {
    this.draftUrl = '';
    this.draftMinutes = DEFAULT_DELAY_MINUTES;
    this.draftMessage = buildDefaultFeedbackSmsMessage(messageName || this.companyName);
    this.urlTouched = false;
    this.messageTouched = false;
    this.minutesTouched = false;
  }

  private applyLocationDraft(row: FeedbackConfigurationRow): void {
    this.draftUrl = row.google_review_url || '';
    this.draftMinutes = parseDelayMinutes(row.feedback_sms_delay_minutes) || DEFAULT_DELAY_MINUTES;
    this.draftMessage =
      row.feedback_sms_message || buildDefaultFeedbackSmsMessage(row.branch || this.companyName);
    this.urlTouched = false;
    this.messageTouched = false;
    this.minutesTouched = false;
  }

  private attachModalToBody(element: HTMLElement): void {
    if (element.parentElement === document.body) {
      return;
    }

    this.bodyScrollBackup = document.body.style.overflow;
    this.renderer.appendChild(document.body, element);
    document.body.style.overflow = 'hidden';
  }

  private restoreBodyScroll(): void {
    document.body.style.overflow = this.bodyScrollBackup;
  }

  private loadLocations(): void {
    this.listSub?.unsubscribe();
    if (!this.companyId) {
      this.locations = [];
      this.loading = false;
      this.errorMessage = 'No company selected.';
      return;
    }

    this.loading = true;
    this.errorMessage = '';
    this.listSub = this.feedbackService.listConfigurations(this.companyId).subscribe({
      next: response => {
        this.loading = false;
        if (response.status && response.data) {
          this.locations = response.data;
          return;
        }

        this.locations = [];
        this.errorMessage = response.message || 'Unable to load feedback configurations.';
      },
      error: err => {
        this.loading = false;
        this.locations = [];
        this.errorMessage =
          err?.error?.message || err?.message || 'Failed to load feedback configurations.';
      },
    });
  }
}
